import { ActiveScanCenterCodeEnum } from '@getcaya/scan-center'
import { ReturnSchedule, ScanCenterCode } from '~app/graph/generated/graphql'
import { selectableReturnIntervals } from './returns-schedule'

/**
 * The centres that currently accept work. Membership comes from the shared
 * enum the API validates against, so retiring a centre there retires it here;
 * filtering the generated enum by it keeps the result typed as ScanCenterCode
 * without casting between two enums TypeScript treats as unrelated.
 */
const ACTIVE_SCAN_CENTER_CODES = new Set<string>(
  Object.values(ActiveScanCenterCodeEnum)
)
const ACTIVE_SCAN_CENTERS = Object.values(ScanCenterCode).filter(code =>
  ACTIVE_SCAN_CENTER_CODES.has(code)
)

export interface ScanCenterOption {
  disabled?: boolean
  label: string
  value: ScanCenterCode
}

/**
 * Builds the scan centre choices for a customer. An account may still sit on a
 * centre that no longer operates; that centre is listed, disabled, so the
 * screen shows where the customer actually is and cannot select it back.
 */
export const getScanCenterOptions = ({
  current,
  getRetiredLabel,
}: {
  current: ScanCenterCode
  getRetiredLabel: (args: { scanCenter: ScanCenterCode }) => string
}): ScanCenterOption[] => {
  const active = ACTIVE_SCAN_CENTERS
  const options: ScanCenterOption[] = active.map(value => ({
    label: value,
    value,
  }))

  return active.includes(current)
    ? options
    : [
        {
          disabled: true,
          label: getRetiredLabel({ scanCenter: current }),
          value: current,
        },
        ...options,
      ]
}

export interface IntervalOption {
  disabled?: boolean
  label: string
  value: ReturnSchedule
}

/**
 * Builds the returns interval choices. A customer already on an interval that
 * is no longer offered keeps it visible and disabled, so the field shows their
 * real setting instead of an empty select they cannot explain.
 */
export const getReturnsIntervalOptions = ({
  current,
  getLabel,
}: {
  current: ReturnSchedule
  getLabel: (args: { interval: ReturnSchedule }) => string
}): IntervalOption[] => {
  const offered = selectableReturnIntervals.map(interval => ({
    label: getLabel({ interval }),
    value: interval,
  }))

  return offered.some(option => option.value === current)
    ? offered
    : [
        {
          disabled: true,
          label: getLabel({ interval: current }),
          value: current,
        },
        ...offered,
      ]
}

export interface ReturnsValues {
  returnsInterval: ReturnSchedule
  returnsNextAt: string | null
  scanCenter: ScanCenterCode
}

export type ConfirmableField = 'returnsInterval' | 'scanCenter'

/**
 * Which pending changes have to be confirmed, in the order they should be
 * read. The next return date is deliberately absent: it is the only field in
 * this section that can change without altering where originals go.
 */
export const getFieldsNeedingConfirmation = ({
  baseline,
  values,
}: {
  baseline: ReturnsValues
  values: ReturnsValues
}): ConfirmableField[] =>
  (['scanCenter', 'returnsInterval'] as const).filter(
    field => baseline[field] !== values[field]
  )

/**
 * Keeps the interval and the next return date consistent as a pure function of
 * the section's values: Off clears the date, and leaving Off needs a date the
 * picker will accept.
 *
 * Both fields travel together on every schedule save: the API replaces the
 * pair rather than patching either, so an omitted date is written as one
 * rather than left alone.
 */
export const applyReturnsIntervalChange = ({
  earliestNextAt,
  returnsInterval,
  returnsNextAt,
}: {
  earliestNextAt: string
  returnsInterval: ReturnSchedule
  returnsNextAt: string | null
}): Pick<ReturnsValues, 'returnsInterval' | 'returnsNextAt'> => {
  if (returnsInterval === ReturnSchedule.Off) {
    return { returnsInterval, returnsNextAt: null }
  }
  return {
    returnsInterval,
    returnsNextAt: returnsNextAt ?? earliestNextAt,
  }
}
